import SectionView from '../views/section-view';
import logging from '../logging';
import { Object3D } from 'three';
import { Section } from '../domain'
import { overlapsDrawer, overlapsPartRange, overlapsGenericAsset  } from './overlapping';
import { approxGteq, approxLteq, round2slot, slotCeil } from '../measurement';

const log = logging('sectionControllers');


/**
 * Tests if the section can accept a drawer with a given height at a given
 * offset in the section.
 * @param section The section to which we want to add a drawer.
 * @param y The world elevation (y coordinate) at which we want to place the
 * new drawer, i.e. the bottom edge of the drawer.
 * @param height The height in inches of the drawer. This must be a multiple of
 * 32mm expressed in inches, but that is not checked here.
 */
export const canAcceptDrawer =
    (section: Section, y: number, height: number): boolean =>
{
    const offset = y;

    // If we intersect any of the existing drawers, we cannot accept a new
    // drawer there.
    if(overlapsDrawer(section, offset, height)) return false;
    if(overlapsPartRange(section, offset, height)) return false;
    if(overlapsGenericAsset(section, offset, height)) return false;

    // The top slot is 32mm increment that the panel above the section is
    // pegged into.
    const topSlot = slotCeil(section.height);
    if(y + height > topSlot) return false;

    // TODO: create overlap procedures to check the door/cubby since presence
    // in the section is no longer enough of a check
    //if(section.cubby) return false;
    //if(section.door) return false;

    if(section.width < 5) return false;
    if(section.height < 5) return false;
    return true;
}

/**
 * Given a three.js object, find the SectionView to which it belongs. If no
 * ancestor SectionView can be found, returns null.
 *
 * Note that if the object itself is a section, then the object is returned, so
 * to get the parent section view of a section view, you should call this on
 * the section's parent instead of the section itself. This has the slight
 * advantage of returning null when the parent is not a section.
 */
export const getParentSectionView = (obj: Object3D): SectionView | null => {
    let item: Object3D | null = obj;
    while(item != null) {
        if(item instanceof SectionView) {
            return item;
        } else {
            item = item.parent;
        }
    }
    return null;
}

/**
 * This is called in the unit editor to adjust positions before every frame
 * rendered.
 */
export const layoutSection = (section: Section): void => {
    for(const door of section.doors) {
        door.width = section.width + 0.5;
        door.position.x = -0.25;
        door.position.z = section.depth;
    }

    for(const cubby of section.cubbies) {
        cubby.width = section.width;
        cubby.depth = section.depth;
    }

    for(const part of section.parametricParts) {
        part.position.y = round2slot(part.position.y);
        part.width = section.width;
    }

    for(const drawer of section.drawers) {
        drawer.width = section.width + 0.5;
        drawer.depth = section.depth;
        drawer.position.x = -0.25;
        drawer.position.y = round2slot(drawer.position.y);
        drawer.position.z = section.depth;
    }
};

/**
 * Check that the given global elevation, y, is inside the given section.
 */
export const isInsideSection = (section: Section, y: number) => {
    const
        bottom = section.position.y,
        top = bottom + section.height;
    return approxGteq(y, bottom) && approxLteq(y, top);
}
